import axios from 'axios';
import {
  getAuthToken,
  saveAuthToken,
  getAdminAuthToken,
  saveAdminAuthToken,
  clearAuthToken,
  resetSessionLocationAndBusinessIds,
} from 'Api';
import * as actions from 'Actions';
import { setErrors } from '../actions';
import converter from 'json-style-converter/es5';
import { isObject } from 'HelperFunctions/types';
import { extractContentFromHtml } from 'HelperFunctions/html';
import { isDefaultVersionGreater } from 'HelperFunctions/version';
import { notifyDatadogAndAirbrake } from '../components/utils/monitoring/notifyAirbrakeAndDatadog';
import { store } from 'Utils/redux/store';
import { history } from 'Components/utils/router/history';
import { SIGN_OUT_USER_SUCCESS } from 'Constants/redux';

const forceSignOut = () => {
  if (!store.getState().auth.authenticated) return;
  clearAuthToken();
  resetSessionLocationAndBusinessIds();
  store.dispatch({ type: SIGN_OUT_USER_SUCCESS });
  store.dispatch(setErrors('Your session has expired. Please log in again.'));
  history.push('/login');
};

const Service = (
  headerFunc = () => {},
  saveAuthFunc = () => {},
  useInjection = false
) => {
  class WrappedService {
    service = () => {
      let service = axios.create({
        headers: headerFunc(),
      });
      service.interceptors.response.use(this.handleSuccess, this.handleError);
      service.defaults.headers.common['X-Version'] =
        import.meta.env.VITE_APP_VERSION;
      axios.defaults.headers.common['X-Version'] =
        import.meta.env.VITE_APP_VERSION;
      return service;
    };

    // Ping Google to check user's internet connection
    checkConnection = async () => {
      return new Promise((resolve) => {
        const img = new Image();
        const timeoutId = setTimeout(() => {
          img.onload = null;
          img.onerror = null;
          resolve(false);
        }, 3000); // 3 second timeout for the ping itself
        
        img.onload = () => {
          clearTimeout(timeoutId);
          resolve(true);
        };
        
        img.onerror = () => {
          clearTimeout(timeoutId);
          resolve(false);
        };
        
        // Use Google's favicon with a cache-busting parameter to ensure a fresh request
        img.src = `https://www.google.com/favicon.ico?t=${Date.now()}`;
      });
    };

    // Check if error is a network error
    isNetworkError = (error) => {
      if (!error) return false;
      
      // Check for common network error indicators
      const isNetworkError =
        error.message === 'Network Error' ||
        error.code === 'ERR_NETWORK' ||
        error.code === 'ECONNABORTED' ||
        error.code === 'ETIMEDOUT' ||
        (!error.response && error.request);
      
      return isNetworkError;
    };

    cleanResponseContent = (response) => {
      if (!!response.data && typeof response.data === 'string') {
        return extractContentFromHtml(response.data);
      }
      return undefined;
    };

    dispatchNonJsonErrorsAndNotify = async (response) => {
      const notifyResult = await notifyDatadogAndAirbrake(
        'Service response error - Non JSON',
        'Service',
        {
          body: this.cleanResponseContent(response),
          url: response?.config?.url,
          method: response?.config?.method,
          status: response?.status,
          contentType: response?.headers?.['content-type'],
        }
      );

      const errorMessage = notifyResult.id
        ? `There was an internal failure processing your request, please try again later. \nIf the problem persists please contact support and provide this error code: ${notifyResult.id}`
        : 'There was an internal failure processing your request, please try again later. \nIf the problem persists please contact support. (non-json response)';

      store.dispatch(setErrors(errorMessage));

    };

    dispatchErrorsAndNotify = async (response, error) => {
      const notifyResult = await notifyDatadogAndAirbrake(
        'Service response error - Axios Error After Call probably a timeout',
        'Service',
        {
          url: error?.config?.url,
          method: error?.config?.method,
          status: error?.response?.status,
          errorMessage: error?.message,
          errorCode: error?.code,
          response,
        }
      );

      const errorMessage = notifyResult.id
        ? `There was an internal failure processing your request, please try again later. \nIf the problem persists please contact support and provide this error code: ${notifyResult.id}`
        : 'There was an internal failure processing your request, please try again later. \nIf the problem persists please contact support.';

      store.dispatch(setErrors(errorMessage));
    };
    dispatchEmptyResponseErrorsAndNotify = async (retryAttempted = false, config = null) => {
      const notifyResult = await notifyDatadogAndAirbrake(
        `Service request error - A null response was passed, this usually means a CORS or other browser based error that stopped the request before sending${
          retryAttempted ? ' (after retry)' : ''
        }`,
        'Service',
        {
          url: config?.url,
          method: config?.method,
        }
      );

      const errorMessage = notifyResult.id
        ? `There was an internal failure processing your request, please try again later. \nIf the problem persists please contact support and provide this error code: ${notifyResult.id}`
        : 'There was an internal failure processing your request, please try again later. \nIf the problem persists please contact support. (empty response)';

      store.dispatch(setErrors(errorMessage));

    };

    //new global handler to take care of checking content-type first BEFORE checking HTTP status
    handleResponse = (response, retryAttempted = false, config = null) => {
      if (!response) {
        this.dispatchEmptyResponseErrorsAndNotify(retryAttempted, config);
        return response;
      }
      const contentType = response.headers['content-type'];
      const responseStatus = parseInt(response.status);

      if (contentType && contentType.startsWith('application/json')) {
        if (responseStatus === 401) {
          forceSignOut();
        } else if (responseStatus === 404) {
          store.dispatch(
            setErrors('The resource you requested is not found - 404.')
          );
          //history.push('/notFound');
        } else if (responseStatus === 429) {
          notifyDatadogAndAirbrake(
            'Service response error - Too many requests',
            'Service',
            {
              url: response?.config?.url,
              method: response?.config?.method,
              status: responseStatus,
              response: response.data,
            }
          );
          store.dispatch(
            setErrors('Too many requests, this IP has exceeded the network limit')
          );
        }
        // TODO add other types of errors?
        return response;
      } else {
        //now we know it was not json let's check HTTP codes
        //first check if it is a known no content and retun an empty JSON object
        if (responseStatus === 204) {
          return response;
        } else if (responseStatus === 401) {
          forceSignOut();
        } else if (responseStatus === 429) {
          notifyDatadogAndAirbrake(
            'Service response error - Too many requests',
            'Service',
            {
              url: response?.config?.url,
              method: response?.config?.method,
              status: responseStatus,
              response: response.data,
            }
          );
          store.dispatch(
            setErrors('Too many requests, this IP has exceeded the network limit')
          );
        } else if (Math.floor(responseStatus / 100) !== 2) {
          this.dispatchNonJsonErrorsAndNotify(response);
        } else {
          //the request is okay, maybe it is a suitable content type that can still be parsed
          if (
            contentType &&
            (contentType.startsWith('text/') ||
              contentType === 'application/javascript' ||
              contentType === 'application/ld+json')
          ) {
            if (!isObject(response.data)) {
              try {
                return {
                  ...response,
                  data: JSON.parse(response.data),
                };
              } catch (e) {
                this.dispatchNonJsonErrorsAndNotify(response);
              }
            }
          } else {
            this.dispatchNonJsonErrorsAndNotify(response);
          }
        }
      }
      return response;
    };

    // Check for updated permissions and update the store
    refreshBusinessPermissions = (response) => {
      if (!response?.data?.updatedBusinessPermissions) return;

      store.dispatch(actions.openLoadingSpinner());

      saveAuthToken(response.headers);
      store.dispatch(
        actions.updateBusinessPermissions(
          converter.snakeToCamelCase(response.data.updatedBusinessPermissions)
        )
      );

      store.dispatch(actions.closeLoadingSpinner());
    };

    handleSuccess = (response) => {
      const newResponse = this.handleResponse(response);

      this.refreshBusinessPermissions(newResponse);

      this.handleVersioning(newResponse);
      if (useInjection) {
        saveAuthFunc(newResponse.headers);
      }
      return newResponse;
    };

    handleError = async (error) => {
      // The error flow will only be applied if the request was not canceled via axios abort controller or cancel token.
      // Otherwise, error.response is undefined and would show error alerts for canceled requests, which can be unintended.
      if (!axios.isCancel(error)) {
        // Check if this is a network error at the first attempt
        if (this.isNetworkError(error) && !error.config.__isRetry) {
          try {
            // Check user's connection by pinging Google
            const isConnected = await this.checkConnection();
            if (isConnected) {
              // Connection is OK but request failed, retry once
              error.config.__isRetry = true;
              return this.service().request(error.config);
            } else {
              // Connection is NOT OK, show user-friendly message
              store.dispatch(
                setErrors('Failed to complete the request, please verify your connection and try again.')
              );
              
              // Don't send to monitoring since it's a user connectivity issue
              console.warn('Network error detected - user appears to be offline');
              return Promise.reject(error);
            } 
          } catch (error) {
            console.error('Error checking connection', error);
          }
        }

        // Check if this is a null response error that should be retried
        if (!error.response && !error.config.__isRetry) {
          // Mark this request as a retry to prevent infinite loops
          error.config.__isRetry = true;

          // Retry the request once using axios directly (bypass interceptors to avoid recursive calls)
          return this.service().request(error.config);
        }

        // For all other errors or retry failures, handle normally
        const retryAttempted = Boolean(error.config.__isRetry);
        const newResponse = this.handleResponse(error.response, retryAttempted, error.config);

        this.handleVersioning(newResponse);
      }

      return Promise.reject(error);
    };

    handleVersioning = (response) => {
      const { open, last_triggered } = store.getState().version;

      if (import.meta.env.VITE_APP_VERSION_CHECK_ENABLED === 'false' || open)
        return;
      if (
        last_triggered &&
        new Date().getTime() - last_triggered <
          parseInt(import.meta.env.VITE_APP_VERSION_CHECK_MODAL_INTERVAL)
      )
        return;
      if (response) {
        const headers = response.headers;
        if (
          headers['version'] &&
          isDefaultVersionGreater(
            headers['version'],
            import.meta.env.VITE_APP_VERSION
          )
        ) {
          if (
            headers['allow-old-version'] &&
            headers['allow-old-version'] == 'false'
          ) {
            window.location.reload();
          } else {
            store.dispatch(actions.openVersionModal());
          }
        }
      }
    };

    redirectTo = (document, path) => {
      document.location = path;
    };

    get(path, params) {
      return this.service().get(path, {
        params,
      });
    }

    delete(path, options) {
      return this.service().delete(path, options);
    }

    patch(path, payload, options = {}) {
      return this.service().request({
        method: 'PATCH',
        url: path,
        responseType: 'json',
        data: payload,
        cancelToken: options.cancelToken,
      });
    }

    put(path, payload, options = {}) {
      return this.service().request({
        method: 'PATCH',
        url: path,
        responseType: 'json',
        data: payload,
        params: options.params,
        cancelToken: options.cancelToken,
      });
    }

    post(path, payload, options = {}) {
      return this.service().request({
        method: 'POST',
        url: path,
        responseType: 'json',
        data: payload,
        headers: options.headers,
        cancelToken: options.cancelToken,
      });
    }
    all(promises) {
      return Promise.all(promises);
    }
    spread(callback) {
      return (arr) => {
        return callback.apply(null, arr);
      };
    }
  }
  return WrappedService;
};

const ServiceClass = Service(getAuthToken, saveAuthToken, true);
export default new ServiceClass();

const AdminServiceClass = Service(getAdminAuthToken, saveAdminAuthToken, true);
export const adminService = new AdminServiceClass();

const StandardServiceClass = Service();
export const standardService = new StandardServiceClass();
